import type {
	CodShortfallReason,
	CodTenderOptions,
	DriverShipment,
	DriverWallet,
} from "@khazzin/api-client";
import type { OutboxEntry } from "@khazzin/api-client/sync";
import type { Money } from "@khazzin/i18n/money";

/**
 * The rules of the cash at the door (EP10-S19), kept apart from the screens
 * so a test can read them: when a stop asks for its cash, what the Confirm
 * gate needs, and what the Cash tab adds up. All money is integer minor
 * units; the phone never computes what is due — that is the server's.
 *
 * Any accepted currency, mixed (COD phase 2a): a payment is tender lines, a
 * currency each, converted to the order's at today's rates (read while online,
 * kept for the day) exactly as the server converts them. No cap.
 */

/** One currency's part of a payment, in its minor units (whole lira). */
export interface TenderLine {
	currency: string;
	amountMinor: number;
}

/** A currency's minor units: 100 for most, 1 for the lira (whole lira), as the server's money package. */
export function minorUnits(currency: string): number {
	if (currency === "LBP") {
		return 1;
	}
	try {
		const digits = new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
			.maximumFractionDigits;
		return 10 ** (digits ?? 2);
	} catch {
		return 100;
	}
}

/**
 * `amountMinor` of `from` in minor units of `to`, through the base: each rate
 * is units per one base unit in millionths. Half away from zero, in BigInt, as
 * the server's `currency.Convert`.
 */
export function convertMinor(
	amountMinor: number,
	from: string,
	fromRate: number,
	to: string,
	toRate: number,
): number {
	if (from === to) {
		return amountMinor;
	}
	const num = BigInt(amountMinor) * BigInt(minorUnits(to)) * BigInt(toRate);
	const den = BigInt(minorUnits(from)) * BigInt(fromRate);
	let q = num / den;
	const r = num % den;
	const twice = (r < 0n ? -r : r) * 2n;
	if (twice >= (den < 0n ? -den : den)) {
		q += num < 0n ? -1n : 1n;
	}
	return Number(q);
}

/** A currency's rate in today's options, or undefined. */
export function rateOf(options: CodTenderOptions | null, currency: string): number | undefined {
	return options?.options?.find((o) => o.currency === currency)?.rateMicro;
}

/**
 * What the lines come to in `to`, or null when a line's currency has no rate
 * today (the phone cannot say, and the server would refuse it).
 */
export function paidMinor(
	lines: readonly TenderLine[],
	to: string,
	options: CodTenderOptions | null,
): number | null {
	let total = 0;
	for (const l of lines) {
		if (l.currency === to) {
			total += l.amountMinor;
			continue;
		}
		const from = rateOf(options, l.currency);
		const toRate = rateOf(options, to);
		if (from === undefined || toRate === undefined) {
			return null;
		}
		total += convertMinor(l.amountMinor, l.currency, from, to, toRate);
	}
	return total;
}

/** The server's `cod.OverpaySlack`: percent over due a partly foreign payment may come. */
export const OVERPAY_SLACK = 1;

/** What a payment books: within the slack over due, paid partly in another currency, it is the amount due. */
export function booked(expectedMinor: number, paid: number, foreign: boolean): number {
	const slack = Math.max(Math.floor((expectedMinor * OVERPAY_SLACK) / 100), 1);
	return foreign && paid > expectedMinor && paid <= expectedMinor + slack ? expectedMinor : paid;
}

/** The queued command that confirms a stop's cash (`POST …/cod:collect`). */
export const COLLECT = "collect_cod";

/** Why less than due was taken, in the order the sheet lists them (CR-025). */
export const SHORTFALL_REASONS: readonly CodShortfallReason[] = [
	"customer_short",
	"discount_agreed_by_store",
	"damaged_item_deduction",
];

/** The statuses a delivery opens its collection in (EP03-S19). */
const DELIVERED = ["delivered", "partially_delivered"];

/** Why Confirm cash is disabled, or `ok`. */
export type Gate = "ok" | "amount" | "over" | "reason";

/**
 * The Confirm gate, the server's `cod.Settle` on the phone: an amount, never
 * more than due (the driver hands back the change), and a reason when short.
 * `tenderedMinor` is what the lines book in the order's currency ([booked]).
 */
export function gate(expectedMinor: number, tenderedMinor: number | null, reason?: string): Gate {
	switch (true) {
		case tenderedMinor === null || tenderedMinor < 0:
			return "amount";
		case (tenderedMinor as number) > expectedMinor:
			return "over";
		case (tenderedMinor as number) < expectedMinor && !reason:
			return "reason";
		default:
			return "ok";
	}
}

/**
 * The `collect_cod` body: the tender lines (an empty line left out). A reason
 * goes only with a short amount — the server refuses one on an exact
 * collection. `bookedMinor` is what the lines book in the order's currency.
 * The idempotency key is copied into `clientEventKey` by the push.
 */
export function collectPayload(
	expectedMinor: number,
	lines: readonly TenderLine[],
	bookedMinor: number,
	reason: CodShortfallReason | undefined,
	at: Date,
): Record<string, unknown> {
	return {
		tenders: lines.filter((l) => l.amountMinor > 0 || lines.length === 1),
		...(bookedMinor < expectedMinor && reason !== undefined ? { shortfallReason: reason } : {}),
		collectedAt: at.toISOString(),
	};
}

/**
 * A queued body's lines: `tenders`, or the one-currency shape queued before
 * them, in `fallback` (the stop's currency) when it names none.
 */
export function payloadLines(payload: Record<string, unknown>, fallback = "USD"): TenderLine[] {
	if (Array.isArray(payload.tenders)) {
		return (payload.tenders as TenderLine[]).map((l) => ({
			currency: String(l.currency),
			amountMinor: Number(l.amountMinor),
		}));
	}
	const currency = typeof payload.tenderCurrency === "string" ? payload.tenderCurrency : fallback;
	return [{ currency, amountMinor: Number(payload.tenderedAmountMinor) }];
}

/** Where a stop's cash stands. */
export type CodStep =
	/** Nothing to collect here: prepaid, not delivered, or already handed over. */
	| { kind: "none" }
	/** Delivered: say what the customer handed over. */
	| { kind: "confirm"; due: Money }
	/** Delivered, but the amount due is the server's and it has not answered yet. */
	| { kind: "awaiting" }
	/** Confirmed on the phone; the server has not had it yet. */
	| { kind: "queued"; collectedMinor: number }
	/** Confirmed and synced: the cash is in the wallet. */
	| { kind: "confirmed"; collectedMinor: number };

const queuedFor = (outbox: readonly OutboxEntry[], command: string, id: string) =>
	outbox.find((e) => e.state === "queued" && e.command === command && e.shipmentId === id);

/**
 * A stop's cash step. The amount due is the server's collection when the
 * wallet has it — for a partial, the kept lines' amount. A full delivery still
 * queued on the phone is due the order's COD, which is what the server opens
 * it at; a partial queued offline waits for the server's figure.
 */
export function codStep(
	stop: DriverShipment,
	wallet: DriverWallet | null,
	outbox: readonly OutboxEntry[],
	options: CodTenderOptions | null = null,
): CodStep {
	// A COD the delivery company pays the store itself (ADJ-1022-6) is
	// collected for the company: nothing to confirm to us.
	if (
		stop.cod === null ||
		stop.cod.amountMinor <= 0 ||
		stop.codPaidToStore === true ||
		!DELIVERED.includes(stop.status)
	) {
		return { kind: "none" };
	}
	const confirmed = queuedFor(outbox, COLLECT, stop.shipmentId);
	if (confirmed !== undefined) {
		const currency = stop.cod.currency;
		const lines = payloadLines(confirmed.payload, currency);
		return { kind: "queued", collectedMinor: paidMinor(lines, currency, options) ?? 0 };
	}
	const held = wallet?.open.find((c) => c.shipmentId === stop.shipmentId);
	if (held !== undefined) {
		return held.status === "pending"
			? { kind: "confirm", due: { amountMinor: held.expectedMinor, currency: held.currency } }
			: { kind: "confirmed", collectedMinor: held.collectedMinor };
	}
	const delivery = queuedFor(outbox, "record_attempt", stop.shipmentId);
	if (delivery?.payload.outcome === "success") {
		return { kind: "confirm", due: stop.cod };
	}
	return delivery !== undefined || wallet === null ? { kind: "awaiting" } : { kind: "none" };
}

/** One line of the Cash tab's list. */
export interface CashRow {
	shipmentId: string;
	trackingNumber: string;
	expectedMinor: number;
	collectedMinor: number;
	currency: string;
	queued: boolean;
	/** The cash itself, a currency each: what the driver holds for this stop. */
	cash: TenderLine[];
}

/**
 * The cash in hand, line by line: the wallet's collections in custody
 * (synced) and the confirmations still queued on the phone. A queued line's
 * amount due is the wallet's pending collection, or the stop's COD.
 */
export function cashRows(
	wallet: DriverWallet | null,
	outbox: readonly OutboxEntry[],
	shipments: Record<string, DriverShipment>,
	options: CodTenderOptions | null = null,
): CashRow[] {
	const open = wallet?.open ?? [];
	const queued = outbox.flatMap((e): CashRow[] => {
		if (e.state !== "queued" || e.command !== COLLECT || e.shipmentId === undefined) {
			return [];
		}
		const id = e.shipmentId;
		const pending = open.find((c) => c.shipmentId === id);
		const stop = shipments[id];
		const known = pending?.currency ?? stop?.cod?.currency;
		const lines = payloadLines(e.payload, known);
		const currency = known ?? lines[0]?.currency ?? "";
		return [
			{
				shipmentId: id,
				trackingNumber: pending?.trackingNumber ?? stop?.trackingNumber ?? "",
				expectedMinor: pending?.expectedMinor ?? stop?.cod?.amountMinor ?? 0,
				collectedMinor: paidMinor(lines, currency, options) ?? 0,
				currency,
				queued: true,
				cash: lines,
			},
		];
	});
	const synced = open
		.filter((c) => c.status === "collected" || c.status === "short_collected")
		.map(
			(c): CashRow => ({
				shipmentId: c.shipmentId,
				trackingNumber: c.trackingNumber,
				expectedMinor: c.expectedMinor,
				collectedMinor: c.collectedMinor,
				currency: c.currency,
				queued: false,
				cash: (c.tenders ?? []).map((t) => ({ currency: t.currency, amountMinor: t.amountMinor })),
			}),
		);
	return [...queued, ...synced];
}

/** Cash in hand per currency: every row's cash, synced and queued alike. */
export function inHand(rows: readonly CashRow[]): Record<string, number> {
	const out: Record<string, number> = {};
	for (const r of rows) {
		const cash =
			r.cash.length > 0 ? r.cash : [{ currency: r.currency, amountMinor: r.collectedMinor }];
		for (const l of cash) {
			out[l.currency] = (out[l.currency] ?? 0) + l.amountMinor;
		}
	}
	return out;
}
